import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMCContext } from './contexts/useMCStates';

const QUILTED_FLAG_LOGO = `${process.env.PUBLIC_URL || ''}/Quilted Flag - Bold Zoom Tilt.png`;
const SCRIPT_CURSIVE_LOGO = `${process.env.PUBLIC_URL || ''}/Stacked Cursive.png`;


interface MCHomePageProps {
    onBrowseEvents: () => void;
}

export const MCHomePage: React.FC<MCHomePageProps> = ({ onBrowseEvents }) => {
    const { appTheme, windowSize } = useMCContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    const computeScale = useCallback(() => {
        if (!contentRef.current || !containerRef.current) return;
        requestAnimationFrame(() => {
            if (!contentRef.current || !containerRef.current) return;
            const appBarH = windowSize.appBarHeight || 60;
            const availH = windowSize.height - appBarH;
            const availW = containerRef.current.clientWidth;
            const contentH = contentRef.current.scrollHeight;
            const contentW = contentRef.current.scrollWidth;
            if (contentH > 0 && availH > 0 && availW > 0) {
                setScale(Math.min(1, availH / contentH, availW / contentW) * 0.96);
            }
        });
    }, [windowSize.height, windowSize.width, windowSize.appBarHeight, windowSize.isMobile]);

    useEffect(() => {
        computeScale();
    }, [computeScale]);

    return (
        <Box
            ref={containerRef}
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                overflow: 'hidden',
                minHeight: 0,
            }}
        >
            <Stack
                ref={contentRef}
                direction="column"
                spacing={8}
                alignItems="center"
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    textAlign: 'center',
                    transform: `scale(${scale})`,
                    transformOrigin: 'top center',
                    marginTop: '10px',
                }}
            >
                <Stack direction='column' spacing={windowSize.isMobile ? 2 : 4} alignItems="center">
                {windowSize.isMobile &&
                <img
                    src={QUILTED_FLAG_LOGO}
                    alt="Moment Central"
                    style={{ height: 120 }}
                />
                }
                <img
                    src={SCRIPT_CURSIVE_LOGO}
                    alt="Moment Golf"
                    style={{ width: 280, objectFit: 'contain' }}
                />
                </Stack>

                <Stack direction="column" spacing={1} alignItems="center">
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: appTheme.palette.primary.main }}
                    >
                        Welcome to Moment Central
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'text.secondary', maxWidth: 380 }}
                    >
                        Your hub for finding, joining, and tracking golf events. Browse upcoming tournaments,
                        track your registrations, revisit leaderboards and standings, and stay connected with your teams.
                    </Typography>
                </Stack>

                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={onBrowseEvents}
                    sx={{ paddingX: 4, fontWeight: 600 }}
                >
                    Browse Events
                </Button>
            </Stack>
        </Box>
    );
};
